// src/components/ui/Toast.jsx
import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, XCircle, Info, Shield } from 'lucide-react';

/**
 * Toast notification component for user feedback
 */
export const Toast = ({
  id,
  type = 'info',
  title,
  message,
  duration = 5000,
  onClose,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.(id);
    }, 300);
  };

  if (!isVisible) return null;

  const getToastStyles = () => {
    const baseStyles = 'flex items-start gap-3 p-4 rounded-lg shadow-lg border max-w-md w-full';
    
    switch (type) {
      case 'success':
        return `${baseStyles} bg-green-50 border-green-200 text-green-800`;
      case 'error':
        return `${baseStyles} bg-red-50 border-red-200 text-red-800`;
      case 'warning':
        return `${baseStyles} bg-orange-50 border-orange-200 text-orange-800`;
      case 'permission':
        return `${baseStyles} bg-purple-50 border-purple-200 text-purple-800`;
      default:
        return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`;
    }
  };

  const getIcon = () => {
    const iconProps = { size: 20, className: 'flex-shrink-0 mt-0.5' };
    
    switch (type) {
      case 'success':
        return <CheckCircle {...iconProps} className="text-green-600" />;
      case 'error':
        return <XCircle {...iconProps} className="text-red-600" />;
      case 'warning':
        return <AlertCircle {...iconProps} className="text-orange-600" />;
      case 'permission':
        return <Shield {...iconProps} className="text-purple-600" />;
      default:
        return <Info {...iconProps} className="text-blue-600" />;
    }
  };

  return (
    <div
      className={`
        ${getToastStyles()}
        ${isExiting ? 'animate-slide-out-right' : 'animate-slide-in-right'}
        ${className}
      `}
    >
      {getIcon()}
      
      <div className="flex-1 min-w-0">
        {title && (
          <h4 className="text-sm font-semibold mb-1">
            {title}
          </h4>
        )}
        {message && (
          <p className="text-sm opacity-90">
            {message}
          </p>
        )}
      </div>

      <button
        onClick={handleClose}
        className="flex-shrink-0 p-1 rounded-md hover:bg-black/10 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
};

/**
 * Toast container component
 */
export const ToastContainer = ({ toasts = [], onRemoveToast }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={onRemoveToast}
        />
      ))}
    </div>
  );
};

/**
 * Hook for managing toast notifications
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Date.now().toString();
    const newToast = { id, ...toast };
    setToasts(prev => [...prev, newToast]);
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const clearToasts = () => {
    setToasts([]);
  };

  // Convenience methods
  const showSuccess = (title, message, options = {}) => {
    return addToast({ type: 'success', title, message, ...options });
  };

  const showError = (title, message, options = {}) => {
    return addToast({ type: 'error', title, message, ...options });
  };

  const showWarning = (title, message, options = {}) => {
    return addToast({ type: 'warning', title, message, ...options });
  };

  const showInfo = (title, message, options = {}) => {
    return addToast({ type: 'info', title, message, ...options });
  };

  const showPermissionError = (title, message, options = {}) => {
    return addToast({ 
      type: 'permission', 
      title: title || 'Access Denied',
      message: message || 'You do not have permission to perform this action.',
      ...options 
    });
  };

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showPermissionError
  };
};

/**
 * Permission-specific toast messages
 */
export const PermissionToasts = {
  accessDenied: (resource = 'this resource') => ({
    type: 'permission',
    title: 'Access Denied',
    message: `You don't have permission to access ${resource}.`
  }),

  insufficientPermissions: (action = 'perform this action') => ({
    type: 'permission',
    title: 'Insufficient Permissions',
    message: `You don't have the required permissions to ${action}.`
  }),

  roleRequired: (role = 'admin') => ({
    type: 'permission',
    title: 'Higher Role Required',
    message: `This action requires ${role} privileges or higher.`
  }),

  loginRequired: () => ({
    type: 'warning',
    title: 'Login Required',
    message: 'Please log in to access this feature.'
  }),

  sessionExpired: () => ({
    type: 'warning',
    title: 'Session Expired',
    message: 'Your session has expired. Please log in again.'
  })
};
